import { useCallback, useEffect, useState } from 'react'

import { AdasBox, AdasSelect, AdasTypography } from '@components/wrapper-components'
import { getOrganisationByReferenceId, getTimeZones, updateTimeZoneOffsetForOrganisation } from 'api/api'
import { useLoadingStore, useOrganisationStore, useUserStore } from '@caradasstore'
import { Globe } from 'lucide-react'
import { Logger } from '../../../logger'

export const TimeZoneSettings = ({ ccId }) => {
  const currentUser = useUserStore((store) => store.currentUser)
  const setOrganisationDetail = useOrganisationStore((store) => store.setOrganisationDetail)
  const setLoading = useLoadingStore((store) => store.setLoading)

  const [timeZones, setTimeZones] = useState([])
  const [selectedTimeZone, setSelectedTimeZone] = useState('')
  const [selectedTimeZoneName, setSelectedTimeZoneName] = useState('')

  // Admins, managers, and owners can edit the time zone; everyone else sees it read-only.
  const canEdit = !!(currentUser?.is_admin || currentUser?.is_manager || currentUser?.is_owner)

  const fetchOrganisation = useCallback(async () => {
    try {
      const { data, status } = await getOrganisationByReferenceId({ ccId })
      if (status === 200 && data?.id) {
        setSelectedTimeZone(data.timezone_at)
        setSelectedTimeZoneName(data.timezone_at_name)
        setOrganisationDetail(data)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'TimeZoneSettings', method: 'getOrganisationByReferenceId', ccId } })
    }
  }, [ccId, setOrganisationDetail])

  const fetchTimeZones = useCallback(async () => {
    try {
      const { data } = await getTimeZones({ ccId })
      if (Array.isArray(data)) {
        setTimeZones(
          data.map(({ timezone_offset, name, timezone_offset_value }) => ({
            id: timezone_offset,
            value: name,
            timezone_offset_value
          }))
        )
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'TimeZoneSettings', method: 'getTimeZones', ccId } })
    }
  }, [ccId])

  useEffect(() => {
    if (ccId > 0) {
      // Only editors need the selectable list; the read-only view just shows the name.
      if (canEdit) fetchTimeZones()
      fetchOrganisation()
    }
  }, [ccId, canEdit, fetchTimeZones, fetchOrganisation])

  const handleChange = async (event) => {
    const next = event.target.value
    setSelectedTimeZone(next)
    setLoading(true)
    try {
      await updateTimeZoneOffsetForOrganisation(ccId, next)
      // Refetch so the organisation store (and every scheduler view reading it) re-renders in the new zone.
      await fetchOrganisation()
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'TimeZoneSettings', method: 'updateTimeZoneOffset', ccId, next } })
    } finally {
      setLoading(false)
    }
  }

  return (
    <AdasBox className='mt-8'>
      <AdasTypography className='mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
        Regional
      </AdasTypography>

      <AdasBox className='flex flex-col gap-4 rounded-2xl border border-border bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between'>
        <AdasBox className='flex items-start gap-3'>
          <span className='inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary/10'>
            <Globe className='h-5 w-5 text-secondary' strokeWidth={1.75} />
          </span>
          <AdasBox className='min-w-0'>
            <AdasTypography className='text-[15px] font-semibold text-foreground'>Time zone</AdasTypography>
            <AdasTypography className='mt-0.5 text-[13px] text-muted-foreground'>
              Drives all scheduler availability windows and appointment times.
            </AdasTypography>
          </AdasBox>
        </AdasBox>

        {canEdit ? (
          <AdasBox className='w-full sm:w-[240px]'>
            <AdasSelect
              labelId='time-zone-label'
              id='time-zone'
              name='time-zone'
              value={selectedTimeZone}
              onChange={handleChange}
              options={timeZones}
              placeholder='Select time zone'
            />
          </AdasBox>
        ) : (
          <AdasTypography className='text-[15px] font-semibold text-foreground sm:text-right'>{selectedTimeZoneName || '—'}</AdasTypography>
        )}
      </AdasBox>
    </AdasBox>
  )
}
